import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CalendarClock, Search } from "lucide-react";
import {
  useLazyListFinancialAidApplicationsForReviewQuery,
  type FinancialAidReviewListItem,
} from "../../../state/domainRouterApi";
import { TailwindDateField } from "../../../components/forms/TailwindDateField";
import { AppDialogFrame } from "../../../components/overlays/AppDialogFrame";
import { SupportErrorMessage } from "../../../components/overlays/EngineeringSupportBanner";
import { useGrantFinancialAidDeadlineExtension } from "../../../financial-aid/hooks/useFinancialAidApi";
import { asArray, asRecord } from "../utils/domainData";
import { toFinancialAidStaffError } from "../utils/errorPresentation";

type DeadlineExtensionsDialogProps = {
  open: boolean;
  applications: readonly FinancialAidReviewListItem[];
  onClose: () => void;
  onGranted: () => Promise<unknown> | unknown;
};

type ExtensionView = {
  id: string;
  familyName: string;
  code: string;
  academicYear: string;
  extendedDeadline: string;
  reason: string;
  grantedBy: string;
  grantedAt: number;
};

type ExtendableApplication = {
  id: string;
  familyName: string;
  code: string;
  academicYear: string;
  status: string;
};

const EXTENDABLE_STATUSES = new Set(["draft", "invited", "opened", "started"]);
const APPLICATION_PAGE_SIZE = 200;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const readString = (value: unknown): string => String(value ?? "").trim();

const tomorrowIsoDate = (): string => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const year = tomorrow.getFullYear();
  const month = `${tomorrow.getMonth() + 1}`.padStart(2, "0");
  const day = `${tomorrow.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDateOnly = (value: string): string => {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[2]}/${match[3]}/${match[1]}` : value;
};

const formatTimestamp = (value: number): string => {
  if (!Number.isFinite(value) || value <= 0) return "Time unavailable";
  const timestampMs = value > 10_000_000_000 ? value : value * 1000;
  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(timestampMs));
};

const projectApplication = (
  item: FinancialAidReviewListItem,
): ExtendableApplication | null => {
  const record = asRecord(item);
  const id = readString(record.id);
  const status = readString(record.status).toLowerCase();
  if (!id || !EXTENDABLE_STATUSES.has(status)) return null;
  return {
    id,
    familyName: readString(record.familyName) || "Unnamed family",
    code: readString(record.code) || id,
    academicYear: readString(record.academicYear),
    status,
  };
};

const projectExtensions = (
  applications: readonly FinancialAidReviewListItem[],
): ExtensionView[] =>
  applications
    .flatMap((item) => {
      const record = asRecord(item);
      const familyName = readString(record.familyName) || "Unnamed family";
      const code = readString(record.code) || readString(record.id);
      return asArray<unknown>(record.deadlineExtensions).flatMap((value) => {
        const extension = asRecord(value);
        const id = readString(extension.id);
        const applicationId = readString(extension.applicationId);
        const extendedDeadline = readString(extension.extendedDeadline);
        if (!id || !applicationId || !extendedDeadline) return [];
        return [
          {
            id,
            familyName,
            code,
            academicYear:
              readString(extension.academicYear) ||
              readString(record.academicYear),
            extendedDeadline,
            reason: readString(extension.reason),
            grantedBy:
              readString(extension.grantedByActorName) ||
              readString(extension.grantedByActorId) ||
              "Administrator",
            grantedAt: Number(extension.grantedAt ?? 0),
          },
        ];
      });
    })
    .sort((left, right) => right.grantedAt - left.grantedAt);

export const DeadlineExtensionsDialog = ({
  open,
  applications,
  onClose,
  onGranted,
}: DeadlineExtensionsDialogProps) => {
  const grantMutation = useGrantFinancialAidDeadlineExtension();
  const [fetchApplicationPage] =
    useLazyListFinancialAidApplicationsForReviewQuery();
  const applicationLoadSequence = useRef(0);
  const [candidateApplications, setCandidateApplications] = useState<
    FinancialAidReviewListItem[]
  >([]);
  const [isLoadingApplications, setIsLoadingApplications] = useState(false);
  const [applicationLoadError, setApplicationLoadError] =
    useState<unknown>(null);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [extendedDeadline, setExtendedDeadline] = useState("");
  const [reason, setReason] = useState("");
  const [feedback, setFeedback] = useState<string | null>(null);

  const loadCandidateApplications = useCallback(async () => {
    const sequence = applicationLoadSequence.current + 1;
    applicationLoadSequence.current = sequence;
    setIsLoadingApplications(true);
    setApplicationLoadError(null);

    try {
      const loaded = new Map<string, FinancialAidReviewListItem>();
      const seenTokens = new Set<string>();
      let nextToken: string | undefined;
      do {
        const page = await fetchApplicationPage({
          limit: APPLICATION_PAGE_SIZE,
          ...(nextToken ? { nextToken } : {}),
          scope: "deadline_extension_candidates",
        }).unwrap();
        page.items.forEach((application) => {
          const id = readString(asRecord(application).id);
          if (id) loaded.set(id, application);
        });
        const returnedToken = readString(page.nextToken);
        if (!returnedToken || seenTokens.has(returnedToken)) {
          nextToken = undefined;
        } else {
          seenTokens.add(returnedToken);
          nextToken = returnedToken;
        }
      } while (nextToken);

      if (applicationLoadSequence.current === sequence) {
        setCandidateApplications([...loaded.values()]);
      }
    } catch (error) {
      if (applicationLoadSequence.current === sequence) {
        setApplicationLoadError(error);
        setCandidateApplications([]);
      }
    } finally {
      if (applicationLoadSequence.current === sequence) {
        setIsLoadingApplications(false);
      }
    }
  }, [fetchApplicationPage]);

  useEffect(() => {
    if (!open) {
      applicationLoadSequence.current += 1;
      return;
    }
    void loadCandidateApplications();
    return () => {
      applicationLoadSequence.current += 1;
    };
  }, [loadCandidateApplications, open]);

  const extendableApplications = useMemo(
    () =>
      candidateApplications
        .map(projectApplication)
        .filter((entry): entry is ExtendableApplication => entry !== null),
    [candidateApplications],
  );
  const visibleApplications = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return extendableApplications;
    return extendableApplications.filter((application) =>
      [application.familyName, application.code, application.academicYear]
        .join(" ")
        .toLowerCase()
        .includes(query),
    );
  }, [extendableApplications, search]);
  const applicationsWithCandidates = useMemo(() => {
    const byId = new Map<string, FinancialAidReviewListItem>();
    [...applications, ...candidateApplications].forEach((application) => {
      const id = readString(asRecord(application).id);
      if (id) byId.set(id, application);
    });
    return [...byId.values()];
  }, [applications, candidateApplications]);
  const extensions = useMemo(
    () => projectExtensions(applicationsWithCandidates),
    [applicationsWithCandidates],
  );
  const selected =
    extendableApplications.find(({ id }) => id === selectedId) ?? null;
  const mutationError = grantMutation.error
    ? toFinancialAidStaffError(
        grantMutation.error,
        "Unable to grant this deadline extension right now.",
      )
    : null;
  const applicationError = applicationLoadError
    ? toFinancialAidStaffError(
        applicationLoadError,
        "Unable to load applications for deadline extensions right now.",
      )
    : null;
  const canGrant = Boolean(
    selected &&
      ISO_DATE_PATTERN.test(extendedDeadline) &&
      reason.trim() &&
      !grantMutation.isPending,
  );

  const resetForm = () => {
    setSelectedId("");
    setExtendedDeadline("");
    setReason("");
    grantMutation.reset();
  };

  const closeDialog = () => {
    resetForm();
    setFeedback(null);
    setSearch("");
    onClose();
  };

  const handleGrant = async () => {
    if (!selected || !canGrant) return;
    try {
      await grantMutation.mutateAsync({
        applicationId: selected.id,
        extendedDeadline,
        reason: reason.trim(),
      });
      await onGranted();
      await loadCandidateApplications();
      setFeedback(
        `${selected.familyName} can submit through ${formatDateOnly(extendedDeadline)} in the school's timezone.`,
      );
      resetForm();
    } catch {
      // The mutation error is rendered below with support-safe details.
    }
  };

  return (
    <AppDialogFrame
      open={open}
      onClose={closeDialog}
      title="Financial aid deadline extensions"
      description="Grant one family additional time without reopening the school-wide cycle. The selected date is inclusive in the school's timezone."
      testId="financial-aid-deadline-extensions-dialog"
      maxWidthClassName="max-w-5xl"
      footer={
        <>
          <button
            type="button"
            onClick={closeDialog}
            disabled={grantMutation.isPending}
            className="px-4 py-2 text-sm font-bold text-irongrey/60 transition-colors hover:text-irongrey disabled:opacity-50"
          >
            Close
          </button>
          <button
            type="button"
            onClick={() => void handleGrant()}
            disabled={!canGrant}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2 text-sm font-bold text-white shadow-lg shadow-primary/20 transition-all hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
            data-testid="financial-aid-deadline-extension-confirm"
          >
            <CalendarClock size={16} aria-hidden="true" />
            {grantMutation.isPending ? "Granting…" : "Grant extension"}
          </button>
        </>
      }
    >
      {feedback ? (
        <p
          role="status"
          className="rounded-xl border border-aquamarine/20 bg-aquamarine/10 px-4 py-3 text-sm font-semibold text-irongrey"
          data-testid="financial-aid-deadline-extension-feedback"
        >
          {feedback}
        </p>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
        <section
          className="space-y-4"
          aria-labelledby="deadline-extension-family-heading"
        >
          <div>
            <h3
              id="deadline-extension-family-heading"
              className="text-sm font-bold text-irongrey"
            >
              1. Choose a family
            </h3>
            <p className="mt-1 text-xs text-text-tertiary">
              Only applications that have not been submitted are listed.
            </p>
          </div>
          <label className="relative block">
            <span className="sr-only">Search applications</span>
            <Search
              size={16}
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary"
            />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search family, code, or year"
              className="w-full rounded-xl border border-border bg-white py-2.5 pl-9 pr-3 text-sm text-irongrey outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/10"
            />
          </label>
          <div className="max-h-52 space-y-2 overflow-y-auto pr-1">
            {applicationError ? (
              <SupportErrorMessage
                technicalDetails={applicationError.technicalDetails}
              >
                {applicationError.message}
              </SupportErrorMessage>
            ) : isLoadingApplications ? (
              <p className="rounded-xl border border-border bg-irongrey/5 px-4 py-5 text-center text-xs text-text-secondary">
                Loading unsubmitted applications…
              </p>
            ) : visibleApplications.length === 0 ? (
              <p className="rounded-xl border border-border bg-irongrey/5 px-4 py-5 text-center text-xs text-text-secondary">
                No unsubmitted applications match this search.
              </p>
            ) : (
              visibleApplications.map((application) => (
                <button
                  key={application.id}
                  type="button"
                  onClick={() => {
                    setSelectedId(application.id);
                    setFeedback(null);
                    grantMutation.reset();
                  }}
                  className={`w-full rounded-xl border px-4 py-3 text-left transition-colors ${
                    selectedId === application.id
                      ? "border-primary bg-primary/5"
                      : "border-border bg-white hover:border-primary/30"
                  }`}
                  aria-pressed={selectedId === application.id}
                >
                  <span className="block text-sm font-bold text-irongrey">
                    {application.familyName}
                  </span>
                  <span className="mt-1 block text-xs text-text-secondary">
                    {application.code} ·{" "}
                    {application.academicYear || "Year unavailable"} ·{" "}
                    {application.status}
                  </span>
                </button>
              ))
            )}
          </div>
        </section>

        <section
          className="space-y-4"
          aria-labelledby="deadline-extension-details-heading"
        >
          <div>
            <h3
              id="deadline-extension-details-heading"
              className="text-sm font-bold text-irongrey"
            >
              2. Set the exception
            </h3>
            <p className="mt-1 text-xs text-text-tertiary">
              The date must be later than the configured cycle hard cutoff.
            </p>
          </div>
          <label className="block">
            <span className="mb-2 block text-[10px] font-bold uppercase tracking-widest text-irongrey/50">
              New deadline
            </span>
            <TailwindDateField
              value={extendedDeadline}
              onValueChange={setExtendedDeadline}
              min={tomorrowIsoDate()}
              disabled={!selected || grantMutation.isPending}
              aria-label="New financial aid submission deadline"
            />
          </label>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-widest text-irongrey/50">
              Reason
              <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-[9px] text-primary">
                Required
              </span>
            </span>
            <textarea
              required
              aria-required="true"
              rows={4}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              disabled={!selected || grantMutation.isPending}
              placeholder="Explain why this family needs additional time. This is saved in the audit trail."
              className="mt-2 w-full rounded-xl border border-border bg-white px-3 py-2 text-sm font-semibold leading-6 text-irongrey outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/10 disabled:opacity-60"
              data-testid="financial-aid-deadline-extension-reason"
            />
          </label>
          {mutationError ? (
            <SupportErrorMessage
              technicalDetails={mutationError.technicalDetails}
            >
              {mutationError.message}
            </SupportErrorMessage>
          ) : null}
        </section>
      </div>

      <section
        className="border-t border-border pt-5"
        aria-labelledby="granted-extensions-heading"
      >
        <h3
          id="granted-extensions-heading"
          className="text-sm font-bold text-irongrey"
        >
          Granted extensions
        </h3>
        <div className="mt-3 max-h-56 space-y-3 overflow-y-auto pr-1">
          {extensions.length === 0 ? (
            <p className="rounded-xl bg-irongrey/5 px-4 py-5 text-center text-xs text-text-secondary">
              No deadline extensions have been granted.
            </p>
          ) : (
            extensions.map((extension) => (
              <article
                key={extension.id}
                className="rounded-xl border border-border bg-white px-4 py-3"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-bold text-irongrey">
                      {extension.familyName}
                    </p>
                    <p className="mt-0.5 text-xs text-text-secondary">
                      {extension.code} ·{" "}
                      {extension.academicYear || "Year unavailable"}
                    </p>
                  </div>
                  <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary">
                    Through {formatDateOnly(extension.extendedDeadline)}
                  </span>
                </div>
                <p className="mt-3 text-sm text-irongrey">{extension.reason}</p>
                <p className="mt-2 text-xs text-text-tertiary">
                  Granted by {extension.grantedBy} ·{" "}
                  {formatTimestamp(extension.grantedAt)}
                </p>
              </article>
            ))
          )}
        </div>
      </section>
    </AppDialogFrame>
  );
};
